/**
 * ArticleAiEnhancements — AI-Overview-friendly editorial blocks at article top.
 *
 * Bundles four lightweight surfaces optimised for AI extraction:
 *   1. Quick Answer    — 2–3 sentence concise summary (from meta_description / hero_excerpt)
 *   2. Key Takeaways   — 3–5 bullets (admin-set article.key_takeaways[] OR auto-derived from H2s)
 *   3. Related Topics  — semantic chips (article.tags + related_keys + condition_key + cluster)
 *   4. Reviewed/Updated — published date · updated date · "Reviewed by Feel Greats editorial"
 *
 * All surfaces are SSR-safe, mobile-fast, and add ZERO new dependencies.
 * Output is pure semantic HTML (h2 / ul / time) so AI crawlers parse it cleanly.
 */
import { Sparkles, ListChecks, BadgeCheck, Tag } from "lucide-react";


const TOPIC_LABELS = {
  pcos: "PCOS",
  insulin_resistance: "Insulin resistance",
  belly_fat: "Belly fat",
  fatty_liver: "Fatty liver",
  menopause: "Menopause",
  perimenopause: "Perimenopause",
  metabolism: "Metabolism",
  fatigue: "Fatigue",
  bloating: "Bloating",
  brain_fog: "Brain fog",
  inflammation: "Inflammation",
  weight_loss: "Weight wellness",
  sleep: "Sleep",
  stress: "Stress",
  hormonal_support: "Hormonal support",
  fatigue_support: "Energy & fatigue",
  gut_health: "Gut health",
  thyroid: "Thyroid",
};


function humaniseTopic(raw) {
  if (!raw) return null;
  const key = String(raw).toLowerCase().replace(/\s+/g, "_");
  if (TOPIC_LABELS[key]) return TOPIC_LABELS[key];
  // Title-case fallback: "insulin_resistance" → "Insulin resistance"
  const s = String(raw).replace(/_/g, " ").trim();
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}


function dedupePreserveOrder(items) {
  const seen = new Set();
  const out = [];
  for (const i of items) {
    if (!i) continue;
    const k = i.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(i);
  }
  return out;
}


function deriveTopicChips(article) {
  const raw = [
    article.condition_key,
    ...(Array.isArray(article.tags) ? article.tags : []),
    ...(Array.isArray(article.related_keys) ? article.related_keys : []),
  ];
  const cleaned = raw.map(humaniseTopic).filter(Boolean);
  return dedupePreserveOrder(cleaned).slice(0, 8);
}


function deriveKeyTakeaways(article) {
  // 1. Use admin-curated key_takeaways[] when present (future field — Phase D-7.5 backend slot).
  if (Array.isArray(article.key_takeaways) && article.key_takeaways.length > 0) {
    return article.key_takeaways.filter((s) => typeof s === "string" && s.trim()).slice(0, 5);
  }
  // 2. Auto-derive from H2 headings in body_md (calm fallback — every published
  //    article has H2s by editorial standard, so this Just Works).
  const body = article.body_md || "";
  const h2s = body
    .split(/\r?\n/)
    .filter((l) => /^##\s+/.test(l))
    .map((l) => l.replace(/^##\s+/, "").trim())
    .filter((s) => s && !/^(faq|sources|references|disclaimer)\b/i.test(s));
  return h2s.slice(0, 5);
}


function deriveQuickAnswer(article) {
  // Strongest concise answer wins. Truncate cleanly at sentence boundary
  // around 280 chars so the AI Overview pulls a tidy block, not a fragment.
  const candidates = [article.meta_description, article.hero_excerpt];
  for (const c of candidates) {
    if (typeof c === "string" && c.trim().length > 40) {
      const clean = c.trim();
      if (clean.length <= 320) return clean;
      // Truncate to last complete sentence within 300 chars
      const cut = clean.slice(0, 300);
      const lastPeriod = Math.max(cut.lastIndexOf(". "), cut.lastIndexOf("? "), cut.lastIndexOf("! "));
      return (lastPeriod > 80 ? cut.slice(0, lastPeriod + 1) : cut).trim();
    }
  }
  return null;
}


function formatLongDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
}


export default function ArticleAiEnhancements({ article }) {
  if (!article) return null;
  const quickAnswer = deriveQuickAnswer(article);
  const takeaways = deriveKeyTakeaways(article);
  const topics = deriveTopicChips(article);
  const publishedAt = formatLongDate(article.created_at);
  const updatedAt = formatLongDate(article.updated_at);
  const showUpdated = updatedAt && updatedAt !== publishedAt;

  // Render nothing if there's literally nothing to show (rare — would mean
  // an article with no meta, no body, no tags).
  if (!quickAnswer && takeaways.length === 0 && topics.length === 0) return null;

  return (
    <section
      className="rounded-3xl border border-emerald-100 bg-white px-5 sm:px-7 py-6 sm:py-7 mb-10 shadow-sm"
      aria-labelledby="ai-quick-answer-heading"
      data-testid="article-ai-enhancements"
    >
      {/* Quick Answer — top, biggest type, AI-extraction friendly */}
      {quickAnswer && (
        <div data-testid="article-quick-answer">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold mb-3">
            <Sparkles className="w-3.5 h-3.5" /> Quick answer
          </div>
          <h2 id="ai-quick-answer-heading" className="sr-only">Quick answer</h2>
          <p className="text-[15px] sm:text-base leading-relaxed text-emerald-950">
            {quickAnswer}
          </p>
        </div>
      )}

      {/* Key Takeaways — bullets, semantic <ul> for AI parsing */}
      {takeaways.length > 0 && (
        <div className={quickAnswer ? "mt-6 pt-5 border-t border-emerald-100/70" : ""} data-testid="article-key-takeaways">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold mb-3">
            <ListChecks className="w-3.5 h-3.5" /> Key takeaways
          </div>
          <ul className="space-y-2">
            {takeaways.map((t, i) => (
              <li
                key={i}
                className="flex items-start gap-2.5 text-[14.5px] text-emerald-900/85 leading-relaxed"
                data-testid={`article-takeaway-${i}`}
              >
                <span aria-hidden className="mt-1.5 inline-block w-1.5 h-1.5 rounded-full bg-emerald-600 shrink-0" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Related Topics — semantic chips */}
      {topics.length > 0 && (
        <div className={(quickAnswer || takeaways.length) ? "mt-6 pt-5 border-t border-emerald-100/70" : ""} data-testid="article-topic-chips">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold mb-3">
            <Tag className="w-3.5 h-3.5" /> Related topics
          </div>
          <ul className="flex flex-wrap gap-1.5" role="list">
            {topics.map((t, i) => (
              <li key={i}>
                <span
                  className="inline-flex items-center text-xs px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-800"
                  data-testid={`article-topic-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {t}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Reviewed + Updated — editorial trust signal for AI Overviews */}
      {(publishedAt || updatedAt) && (
        <div
          className="mt-6 pt-5 border-t border-emerald-100/70 flex flex-wrap items-center gap-x-4 gap-y-2 text-[11.5px] text-emerald-900/65"
          data-testid="article-reviewed-block"
        >
          <span className="inline-flex items-center gap-1.5 text-emerald-800">
            <BadgeCheck className="w-3.5 h-3.5" />
            <span className="font-semibold">Reviewed by Feel Greats Editorial</span>
          </span>
          {publishedAt && (
            <span className="inline-flex items-center gap-1.5">
              <span className="text-emerald-700/40" aria-hidden>·</span>
              Published <time dateTime={article.created_at}>{publishedAt}</time>
            </span>
          )}
          {showUpdated && (
            <span className="inline-flex items-center gap-1.5">
              <span className="text-emerald-700/40" aria-hidden>·</span>
              Updated <time dateTime={article.updated_at}>{updatedAt}</time>
            </span>
          )}
        </div>
      )}
    </section>
  );
}
